import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  deleteFlowFS,
  listFlowFS,
  moveFlowFS,
  readFlowFS,
  statFlowFS,
  writeBytesFlowFS,
  writeJsonFlowFS,
} from "./api/flowfs";
import { useFlowFSStore } from "./state/flowfs-store";
import { toast } from "sonner";

export function useFlowFSList(
  tenantId: string,
  flowId: string,
  params: { prefix: string; recursive: boolean; limit: number; cursor?: string }
) {
  return useQuery({
    enabled: Boolean(tenantId) && Boolean(flowId) && Boolean(params.prefix),
    queryKey: [
      "flowfs",
      "list",
      tenantId,
      flowId,
      params.prefix,
      params.recursive,
      params.limit,
      params.cursor,
    ],
    queryFn: () => listFlowFS({ tenantId, flowId, ...params }),
    staleTime: 5000,
    retry: (count, err: any) => (err?.status === 403 ? false : count < 2),
  });
}

export function useFlowFSStat(tenantId: string, flowId: string, path?: string) {
  const setEtag = useFlowFSStore((s) => s.setEtag);
  return useQuery({
    enabled: !!path,
    queryKey: ["flowfs", "stat", tenantId, flowId, path],
    queryFn: async () => statFlowFS({ tenantId, flowId, path: path! }),
    onSuccess: (r) => {
      if (path) setEtag(path, r.etag);
    },
  });
}

export function useFlowFSRead(
  tenantId: string,
  flowId: string,
  path?: string,
  as: "json" | "text" = "json"
) {
  const setEtag = useFlowFSStore((s) => s.setEtag);
  return useQuery({
    enabled: !!path,
    queryKey: ["flowfs", "read", tenantId, flowId, path, as],
    queryFn: async () => readFlowFS({ tenantId, flowId, path: path!, as }),
    onSuccess: (r) => {
      if (path) setEtag(path, r.etag);
    },
  });
}

export function useWriteJson(tenantId: string, flowId: string) {
  const qc = useQueryClient();
  const getEtag = (path: string) => useFlowFSStore.getState().etagByPath.get(path);
  return useMutation({
    mutationFn: async (p: {
      path: string;
      data: any;
      as?: "json" | "text";
      createOnly?: boolean;
      etag?: string;
      sensitive?: boolean;
      contentType?: string;
    }) => writeJsonFlowFS({ tenantId, flowId, ...p, etag: p.etag ?? getEtag(p.path) }),
    onSuccess: (_, p) => {
      qc.invalidateQueries({ queryKey: ["flowfs", "list", tenantId, flowId] });
      qc.invalidateQueries({ queryKey: ["flowfs", "stat", tenantId, flowId, p.path] });
      qc.invalidateQueries({ queryKey: ["flowfs", "read", tenantId, flowId, p.path] });
      toast.success("Saved");
    },
    onError: (err: any) => {
      if (err?.status === 412) toast.error("ETag mismatch. File changed elsewhere.");
      else toast.error(err?.message || "Save failed");
    },
  });
}

export function useWriteBytes(tenantId: string, flowId: string) {
  const qc = useQueryClient();
  const getEtag = (path: string) => useFlowFSStore.getState().etagByPath.get(path);
  return useMutation({
    mutationFn: async (p: {
      path: string;
      bytes: Blob | ArrayBuffer | Uint8Array;
      contentType: string;
      createOnly?: boolean;
      etag?: string;
    }) => writeBytesFlowFS({ tenantId, flowId, ...p, etag: p.etag ?? getEtag(p.path) }),
    onSuccess: (_, p) => {
      qc.invalidateQueries({ queryKey: ["flowfs", "list", tenantId, flowId] });
      qc.invalidateQueries({ queryKey: ["flowfs", "stat", tenantId, flowId, p.path] });
      toast.success("Uploaded");
    },
    onError: (err: any) => {
      if (err?.status === 412) toast.error("ETag mismatch. File changed elsewhere.");
      else toast.error(err?.message || "Upload failed");
    },
  });
}

export function useMove(tenantId: string, flowId: string) {
  const qc = useQueryClient();
  const getEtag = (path: string) => useFlowFSStore.getState().etagByPath.get(path);
  return useMutation({
    mutationFn: async (p: { src: string; dst: string; overwrite: boolean; etag?: string }) =>
      moveFlowFS({ tenantId, flowId, ...p, etag: p.etag ?? getEtag(p.src) }),
    onSuccess: (_, p) => {
      qc.invalidateQueries({ queryKey: ["flowfs", "list", tenantId, flowId] });
      qc.invalidateQueries({ queryKey: ["flowfs", "stat", tenantId, flowId, p.src] });
      qc.invalidateQueries({ queryKey: ["flowfs", "stat", tenantId, flowId, p.dst] });
      toast.success("Moved");
    },
    onError: (err: any) => {
      if (err?.status === 409) toast.error("Destination exists.");
      else if (err?.status === 412) toast.error("ETag mismatch.");
      else toast.error(err?.message || "Move failed");
    },
  });
}

export function useDelete(tenantId: string, flowId: string) {
  const qc = useQueryClient();
  const getEtag = (path: string) => useFlowFSStore.getState().etagByPath.get(path);
  return useMutation({
    mutationFn: async (p: { path: string; recursive?: boolean; etag?: string }) =>
      deleteFlowFS({ tenantId, flowId, ...p, etag: p.etag ?? getEtag(p.path) }),
    onSuccess: (_, p) => {
      qc.invalidateQueries({ queryKey: ["flowfs", "list", tenantId, flowId] });
      qc.invalidateQueries({ queryKey: ["flowfs", "stat", tenantId, flowId, p.path] });
      toast.success("Deleted");
    },
    onError: (err: any) => {
      if (err?.status === 404) toast.error("Path not found.");
      else if (err?.status === 412) toast.error("ETag mismatch.");
      else toast.error(err?.message || "Delete failed");
    },
  });
}
